import { typeEffectiveness } from '../constants';

export enum HazardType {
    SPIKES = 'spikes',
    TOXIC_SPIKES = 'poison_spikes',
    STICKY_WEB = 'sticky_web',
    STEALTH_ROCK = 'pointed_stones',
}

/**
 * Entry hazards object where keys are hazard types and values are the number of layers.
 * Example: { [HazardType.SPIKES]: 3, [HazardType.TOXIC_SPIKES]: 2 }
 */
export type EntryHazards = Partial<Record<HazardType, number>>;

/**
 * Calculate damage dealt by entry hazards when a Pokemon enters the field.
 * @param hazardType - The type of entry hazard
 * @param layers - The number of layers of this hazard (1-3 for spikes, 1-2 for toxic spikes, etc.)
 * @param pokemon - The Pokemon instance (needed for Stealth Rock type effectiveness)
 * @returns The damage amount
 */
export function calculateEntryHazardDamage(
    hazardType: HazardType,
    layers: number,
    pokemon: { stats: { maxHp: number }; type1: string; type2?: string }
): number {
    switch (hazardType) {
        case HazardType.SPIKES: {
            // Spikes: 1/8, 1/6, 1/4 of max HP for 1, 2, 3 layers respectively
            const spikesMultipliers = [1 / 8, 1 / 6, 1 / 4];
            const spikesLayers = Math.min(layers, 3);
            return Math.ceil(pokemon.stats.maxHp * spikesMultipliers[spikesLayers - 1]);
        }
        case HazardType.STEALTH_ROCK: {
            // Stealth Rock: 1/8 of max HP, but type effectiveness applies (Rock-type damage)
            const baseDamage = pokemon.stats.maxHp * (1 / 8);

            // Calculate type effectiveness (Rock-type against Pokemon's types)
            const rockEffectiveness = typeEffectiveness.rock as Record<string, number>;
            const effect1 = rockEffectiveness[pokemon.type1] ?? 1;
            const effect2 = pokemon.type2 !== undefined ? rockEffectiveness[pokemon.type2] ?? 1 : 1;

            // If both types are the same, don't double count
            const typeMultiplier = pokemon.type1 === pokemon.type2 ? effect1 : effect1 * effect2;

            return Math.ceil(baseDamage * typeMultiplier);
        }
        case HazardType.STICKY_WEB:
            // Sticky Web doesn't deal damage, only lowers speed
            return 0;
        case HazardType.TOXIC_SPIKES:
            // Toxic Spikes doesn't deal damage, only applies poison status
            return 0;
        default:
            return 0;
    }
}

/**
 * Calculate status effects applied by entry hazards.
 * @param hazardType - The type of entry hazard
 * @param layers - The number of layers of this hazard
 * @returns The status effect to apply, or null if none
 */
export function calculateEntryHazardStatusEffect(hazardType: HazardType, layers: number): string | null {
    switch (hazardType) {
        case HazardType.TOXIC_SPIKES:
            // 1 layer: regular poison, 2 layers: toxic poison
            if (layers >= 2) {
                return 'bad_poison';
            } else if (layers >= 1) {
                return 'poison';
            }
            return null;
        case HazardType.SPIKES:
        case HazardType.STEALTH_ROCK:
        case HazardType.STICKY_WEB:
            // These don't apply status effects
            return null;
        default:
            return null;
    }
}

/**
 * Calculate stat modifications from entry hazards.
 * @param hazardType - The type of entry hazard
 * @returns Object with stat modifications, or null if none
 */
export function calculateEntryHazardStatModification(hazardType: HazardType): {
    stat: string;
    stages: number;
} | null {
    switch (hazardType) {
        case HazardType.STICKY_WEB:
            // Sticky Web lowers Speed by 1 stage
            return { stat: 'speed', stages: -1 };
        case HazardType.SPIKES:
        case HazardType.STEALTH_ROCK:
        case HazardType.TOXIC_SPIKES:
            // These don't modify stats
            return null;
        default:
            return null;
    }
}
